import React, { useState, useEffect } from 'react';

// Owner-authored "deal packet" — a sales-safe marketing write-up shown to any agent from the
// Pipeline. One component, two modes driven by the API's can_edit: the deal owner / org admins get
// editable fields + Save; everyone else gets a read-only view. The packet holds plain text only
// (no MAO/fee/price), so it's safe for the whole sales team to read.

// Keep keys in sync with PACKET_FIELDS in backend/routes/deals.js.
const FIELDS = [
  { key: 'headline',      label: 'Headline',                    placeholder: 'One-line hook — e.g. "3/2 cosmetic flip in hot 78704 pocket"', rows: 2 },
  { key: 'overview',      label: 'Property Overview',           placeholder: 'Layout, standout features, what makes this a deal…', rows: 4 },
  { key: 'condition',     label: 'Condition Notes',             placeholder: 'Current condition, what works / what doesn’t…', rows: 3 },
  { key: 'rehab_scope',   label: 'Rehab Scope',                 placeholder: 'Scope of work — kitchen, baths, flooring, roof, mechanicals…', rows: 3 },
  { key: 'exit_strategy', label: 'Recommended Exit Strategy',   placeholder: 'Flip / rental / BRRRR and why…', rows: 2 },
  { key: 'neighborhood',  label: 'Neighborhood / Market',       placeholder: 'Area, schools, comps trend, demand…', rows: 3 },
  { key: 'access',        label: 'Showing / Access',            placeholder: 'Lockbox, occupancy, how to show…', rows: 2 },
  { key: 'notes',         label: 'Additional Notes',            placeholder: 'Anything else the buyer should know…', rows: 3 },
];

export default function DealPacketModal({ dealId, dealAddress, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [canEdit, setCanEdit] = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetch(`/api/deals/${dealId}/packet`).then(r => r.json())
      .then(d => { if (!cancelled) { setForm(d.packet || {}); setCanEdit(!!d.can_edit); setUpdatedAt(d.updated_at); setLoading(false); } })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [dealId]);

  async function save() {
    setSaving(true);
    try {
      const r = await fetch(`/api/deals/${dealId}/packet`, {
        method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ packet: form }),
      });
      const d = await r.json();
      if (!r.ok || d.error) throw new Error(d.error || 'failed');
      if (onSaved) onSaved(d);
      onClose();
    } catch (e) { alert(`Save failed: ${e.message}`); }
    setSaving(false);
  }

  const hasAny = FIELDS.some(f => form[f.key] && String(form[f.key]).trim());

  return (
    <div style={overlay} onClick={onClose}>
      <div style={panel} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '18px 20px', borderBottom: '1px solid var(--navy-border)' }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--white)' }}>Deal Packet{canEdit ? ' — Edit' : ''}</div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 3 }}>
              {dealAddress || ''}{updatedAt ? ` · updated ${String(updatedAt).split('T')[0]}` : ''}
            </div>
          </div>
          <button onClick={onClose} style={closeBtn}>×</button>
        </div>

        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          {loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>Loading packet…</div>
          ) : canEdit ? (
            FIELDS.map(f => (
              <div key={f.key} style={{ marginBottom: 14 }}>
                <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 6 }}>{f.label}</label>
                <textarea value={form[f.key] || ''} rows={f.rows} placeholder={f.placeholder}
                  onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                  style={{ width: '100%', boxSizing: 'border-box', padding: '9px 12px', borderRadius: 8, border: '1px solid var(--navy-border)', background: 'var(--navy)', color: 'var(--white)', fontSize: 13, lineHeight: 1.5, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>
            ))
          ) : !hasAny ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--gray-500)', fontSize: 13 }}>The deal owner hasn’t filled out a packet for this deal yet.</div>
          ) : (
            FIELDS.filter(f => form[f.key] && String(form[f.key]).trim()).map(f => (
              <div key={f.key} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--gray-500)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 5 }}>{f.label}</div>
                <div style={{ fontSize: 14, color: 'var(--gray-300)', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>{form[f.key]}</div>
              </div>
            ))
          )}
        </div>

        <div style={{ padding: '14px 20px', borderTop: '1px solid var(--navy-border)', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} style={ghostBtn}>{canEdit ? 'Cancel' : 'Close'}</button>
          {canEdit && (
            <button onClick={save} disabled={saving} style={{ ...primaryBtn, opacity: saving ? 0.5 : 1, cursor: saving ? 'default' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save Packet'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const overlay  = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const panel    = { background: 'var(--navy-card)', border: '1px solid var(--navy-border)', borderRadius: 14, width: 'min(640px, 100%)', maxHeight: '85vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.4)' };
const closeBtn = { background: 'transparent', border: 'none', color: 'var(--gray-500)', fontSize: 24, lineHeight: 1, cursor: 'pointer', padding: 0 };
const ghostBtn = { padding: '8px 16px', borderRadius: 8, background: 'transparent', border: '1px solid var(--navy-border)', color: 'var(--gray-300)', fontSize: 13, fontWeight: 700, cursor: 'pointer' };
const primaryBtn = { padding: '8px 18px', borderRadius: 8, background: 'var(--teal)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 700 };
